import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Orders() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'current' | 'completed' | 'cancelled'>('current');

  const currentOrders = [
    {
      id: 1,
      orderNumber: '#12345',
      date: '٢٠٢٤/٠١/١٥',
      time: '14:30',
      status: 'في الطريق',
      items: 3,
      total: 167,
      provider: 'بقالة الخير',
      statusColor: 'text-[#FF8F00]',
      bgColor: 'bg-[#FF8F00]/10'
    },
    {
      id: 2,
      orderNumber: '#12344',
      date: '٢٠٢٤/٠١/١٥',
      time: '12:15',
      status: 'قيد التجهيز',
      items: 5,
      total: 245,
      provider: 'متجر الطازج',
      statusColor: 'text-[#0D47A1]',
      bgColor: 'bg-[#0D47A1]/10'
    }
  ];

  const completedOrders = [
    {
      id: 3,
      orderNumber: '#12343',
      date: '٢٠٢٤/٠١/١٤',
      time: '18:45',
      status: 'تم التسليم',
      items: 4,
      total: 198,
      provider: 'سوبر ماركت النخيل',
      statusColor: 'text-[#4CAF50]',
      bgColor: 'bg-[#4CAF50]/10',
      rated: true
    },
    {
      id: 4,
      orderNumber: '#12342',
      date: '٢٠٢٤/٠١/١٣',
      time: '16:20',
      status: 'تم التسليم',
      items: 6,
      total: 312,
      provider: 'بقالة الخير',
      statusColor: 'text-[#4CAF50]',
      bgColor: 'bg-[#4CAF50]/10',
      rated: false
    }
  ];

  const cancelledOrders = [
    {
      id: 5,
      orderNumber: '#12341',
      date: '٢٠٢٤/٠١/١٢',
      time: '14:00',
      status: 'ملغي',
      items: 2,
      total: 89,
      provider: 'متجر الطازج',
      statusColor: 'text-gray-500',
      bgColor: 'bg-gray-100',
      reason: 'المنتج غير متوفر'
    }
  ];

  const getOrders = () => {
    switch (activeTab) {
      case 'current':
        return currentOrders;
      case 'completed':
        return completedOrders;
      case 'cancelled':
        return cancelledOrders;
      default:
        return [];
    }
  };

  const orders = getOrders();

  return (
    <div className="min-h-screen bg-[#F6F6F6] pb-20" style={{ fontFamily: 'Cairo, sans-serif' }}>
      {/* Top Navigation */}
      <div className="fixed top-0 left-0 right-0 bg-white z-50 shadow-sm">
        <div className="px-4 py-4">
          <h1 className="text-[#212121] text-lg font-bold text-center">طلباتي</h1>
        </div>

        {/* Tabs */}
        <div className="px-4 pb-3">
          <div className="bg-[#F6F6F6] rounded-xl p-1 grid grid-cols-3 gap-1">
            <button
              onClick={() => setActiveTab('current')}
              className={`py-2.5 rounded-lg font-medium text-sm transition-all ${
                activeTab === 'current'
                  ? 'bg-white text-[#0D47A1] shadow-sm'
                  : 'text-gray-600'
              }`}
            >
              الحالية ({currentOrders.length})
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`py-2.5 rounded-lg font-medium text-sm transition-all ${
                activeTab === 'completed'
                  ? 'bg-white text-[#0D47A1] shadow-sm'
                  : 'text-gray-600'
              }`}
            >
              المكتملة ({completedOrders.length})
            </button>
            <button
              onClick={() => setActiveTab('cancelled')}
              className={`py-2.5 rounded-lg font-medium text-sm transition-all ${
                activeTab === 'cancelled'
                  ? 'bg-white text-[#0D47A1] shadow-sm'
                  : 'text-gray-600'
              }`}
            >
              الملغية ({cancelledOrders.length})
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="pt-32 px-4">
        {orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center mb-6">
              <i className="ri-file-list-line text-gray-300 text-6xl"></i>
            </div>
            <h2 className="text-[#212121] text-xl font-bold mb-2">لا توجد طلبات</h2>
            <p className="text-gray-500 text-sm text-center mb-8">
              {activeTab === 'current' && 'ليس لديك طلبات حالية'}
              {activeTab === 'completed' && 'ليس لديك طلبات مكتملة'}
              {activeTab === 'cancelled' && 'ليس لديك طلبات ملغية'}
            </p>
            {activeTab === 'current' && (
              <button
                onClick={() => navigate('/products')}
                className="bg-gradient-to-r from-[#0D47A1] to-[#1976D2] text-white px-8 py-3 rounded-xl font-bold shadow-lg active:scale-95 transition-transform"
              >
                ابدأ التسوق
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-2xl p-5 shadow-sm">
                {/* Order Header */}
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
                  <div>
                    <h3 className="text-[#212121] font-bold mb-1">{order.orderNumber}</h3>
                    <p className="text-gray-500 text-xs">{order.date} • {order.time}</p>
                  </div>
                  <div className={`${order.bgColor} ${order.statusColor} px-3 py-1.5 rounded-full text-xs font-bold`}>
                    {order.status}
                  </div>
                </div>

                {/* Order Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">المتجر</span>
                    <span className="text-[#212121] font-medium">{order.provider}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">عدد المنتجات</span>
                    <span className="text-[#212121] font-medium">{order.items} منتجات</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">المبلغ الإجمالي</span>
                    <span className="text-[#0D47A1] font-bold">{order.total} ر.س</span>
                  </div>
                  {order.reason && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">سبب الإلغاء</span>
                      <span className="text-gray-500 font-medium">{order.reason}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-2">
                  {activeTab === 'current' && (
                    <>
                      <button
                        onClick={() => navigate('/tracking')}
                        className="bg-gradient-to-r from-[#0D47A1] to-[#1976D2] text-white py-2.5 rounded-xl font-bold text-sm active:scale-95 transition-transform"
                      >
                        تتبع الطلب
                      </button>
                      <button className="bg-gray-100 text-gray-700 py-2.5 rounded-xl font-bold text-sm active:scale-95 transition-transform">
                        إلغاء الطلب
                      </button>
                    </>
                  )}
                  {activeTab === 'completed' && (
                    <>
                      <button className="bg-[#F6F6F6] text-[#212121] py-2.5 rounded-xl font-bold text-sm active:scale-95 transition-transform">
                        عرض التفاصيل
                      </button>
                      {!order.rated ? (
                        <button
                          onClick={() => navigate('/rating')}
                          className="bg-gradient-to-r from-[#0D47A1] to-[#1976D2] text-white py-2.5 rounded-xl font-bold text-sm active:scale-95 transition-transform"
                        >
                          تقييم الطلب
                        </button>
                      ) : (
                        <button className="bg-[#4CAF50] text-white py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-1">
                          <i className="ri-star-fill"></i>
                          تم التقييم
                        </button>
                      )}
                    </>
                  )}
                  {activeTab === 'cancelled' && (
                    <>
                      <button className="bg-[#F6F6F6] text-[#212121] py-2.5 rounded-xl font-bold text-sm active:scale-95 transition-transform">
                        عرض التفاصيل
                      </button>
                      <button
                        onClick={() => navigate('/products')}
                        className="bg-gradient-to-r from-[#0D47A1] to-[#1976D2] text-white py-2.5 rounded-xl font-bold text-sm active:scale-95 transition-transform"
                      >
                        إعادة الطلب
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
        <div className="grid grid-cols-5 h-16">
          <button onClick={() => navigate('/home')} className="flex flex-col items-center justify-center gap-1">
            <i className="ri-home-5-line text-gray-400 text-xl"></i>
            <span className="text-gray-400 text-[0.625rem]">الرئيسية</span>
          </button>
          <button onClick={() => navigate('/products')} className="flex flex-col items-center justify-center gap-1">
            <i className="ri-grid-line text-gray-400 text-xl"></i>
            <span className="text-gray-400 text-[0.625rem]">المنتجات</span>
          </button>
          <button onClick={() => navigate('/cart')} className="flex flex-col items-center justify-center gap-1 relative">
            <i className="ri-shopping-cart-line text-gray-400 text-xl"></i>
            <span className="text-gray-400 text-[0.625rem]">السلة</span>
            <span className="absolute top-1 right-1/2 translate-x-3 w-4 h-4 bg-[#FF8F00] text-white text-[0.5rem] font-bold rounded-full flex items-center justify-center">3</span>
          </button>
          <button className="flex flex-col items-center justify-center gap-1">
            <i className="ri-file-list-fill text-[#0D47A1] text-xl"></i>
            <span className="text-[#0D47A1] text-[0.625rem] font-medium">الطلبات</span>
          </button>
          <button onClick={() => navigate('/profile')} className="flex flex-col items-center justify-center gap-1">
            <i className="ri-user-line text-gray-400 text-xl"></i>
            <span className="text-gray-400 text-[0.625rem]">الحساب</span>
          </button>
        </div>
      </div>
    </div>
  );
}